import React, { useState } from "react";
import { CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useHoneypot } from "./Honeypot";
import { MarketingOptIn, OPT_IN_NEEDS_EMAIL } from "./MarketingOptIn";
import { trackEnquiryConversion } from "../lib/analytics";

/** Renders the migrated contact form definition and posts it as an enquiry. */
export default function ContactForm({ page }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    rego: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const { honeypot, HoneypotField } = useHoneypot();
  const [optIn, setOptIn] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.rego.trim()) {
      toast.error("Please fill in your name, phone number and rego");
      return;
    }
    if (optIn && !form.email.trim()) {
      toast.error(OPT_IN_NEEDS_EMAIL);
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        rego: form.rego,
        website: honeypot,
        marketing_opt_in: optIn,
        message: form.message.trim() || `Enquiry from ${form.name} regarding rego ${form.rego}`,
        vehicle: "",
        ref_type: "page",
        ref_id: page?.slug || "",
        ref_slug: page?.slug || "",
        ref_title: page?.title || "",
      };
      // Only include email if it's not empty
      if (form.email.trim()) {
        payload.email = form.email;
      }
      const { data } = await api.post("/enquiries", payload);
      setDone(data.enquiry.reference);
      trackEnquiryConversion({ source: "contact_form", email: form.email, phone: form.phone });
      toast.success("Message sent", { description: "We'll be in touch shortly." });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      let errorMsg = "Please try again.";
      if (typeof detail === "string") {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        // Pydantic validation error format
        errorMsg = detail.map(e => e.msg || e.message || "Validation error").join(", ");
      }
      toast.error("Could not send", { description: errorMsg });
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="card-nq p-8 text-center" data-testid="contact-form-success">
        <CheckCircle2 className="mx-auto h-10 w-10 gold-text" />
        <h3 className="mt-3 text-xl font-semibold">Message received</h3>
        <p className="mt-2 text-sm text-[color:var(--nq-stone)]">
          Your reference is <span className="font-mono gold-text">{done}</span>. We&apos;ll get back to
          you as soon as we can.
        </p>
      </div>
    );
  }

  const field =
    "w-full rounded-lg border bg-transparent px-3 py-2.5 text-sm outline-none focus:border-[color:var(--nq-gold)]";
  const labelCls =
    "mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[color:var(--nq-stone)]";
  const bd = { borderColor: "rgba(255,255,255,0.14)" };

  return (
    <div className="card-nq p-6 md:p-8">
      <p className="eyebrow">Send a message</p>
      <h2 className="mt-2 text-2xl font-bold">Get in touch</h2>

      <form className="mt-6 grid gap-4 sm:grid-cols-2" onSubmit={submit} data-testid="contact-form">
        {HoneypotField}
        <div>
          <label className={labelCls}>Name *</label>
          <input value={form.name} onChange={set("name")} required className={field} style={bd} data-testid="contact-name-input" />
        </div>
        <div>
          <label className={labelCls}>Email</label>
          <input type="email" value={form.email} onChange={set("email")} className={field} style={bd} data-testid="contact-email-input" />
        </div>
        <div>
          <label className={labelCls}>Phone number *</label>
          <input value={form.phone} onChange={set("phone")} required inputMode="tel" className={field} style={bd} data-testid="contact-phone-input" />
        </div>
        <div>
          <label className={labelCls}>Rego number *</label>
          <input
            value={form.rego}
            onChange={set("rego")}
            required
            placeholder="We'll look up your vehicle from this"
            className={field}
            style={bd}
            data-testid="contact-rego-input"
          />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Comment or message</label>
          <textarea rows={5} value={form.message} onChange={set("message")} className={field} style={bd} data-testid="contact-message-input" />
        </div>
        <div className="sm:col-span-2">
          <MarketingOptIn id="contact-marketing-opt-in" checked={optIn} onChange={setOptIn} />
        </div>
        <div className="sm:col-span-2">
          <button type="submit" disabled={busy} className="btn-gold px-6 py-3 disabled:opacity-60" data-testid="contact-submit-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {busy ? "Sending..." : "Send message"}
          </button>
        </div>
      </form>
    </div>
  );
}
